import {
  Breadcrumb,
  Button,
  Drawer,
  LegacyForm,
  Page,
  Progress,
  Space,
  usePaypingContext,
} from "@payping/web-components";
import { Close } from "@payping/icons";
import { DashboardRoutes } from "@/router";
import { SubLayout } from "@/components/SubLayout";
import { FormLayout } from "@/components/DashDigitalAccount/FormLayout";
import { useAppDispatch, useAppTranslation } from "@/utils";
import { Link, generatePath, useNavigate, useParams } from "react-router-dom";
import FirstStepSaman from "@/pages/DigitalAccount/Saman/Create/FirstStep";
import SecondStepSaman, { SamanRowType } from "@/pages/DigitalAccount/Saman/Create/SecondStep";
import ThirdStepSaman from "@/pages/DigitalAccount/Saman/Create/ThirdStep";
import DataCardStepSaman from "@/pages/DigitalAccount/Saman/Create/Anchor/DataCardStepSaman";
import { useEffect, useState } from "react";
import { TransferMethodEnum } from "@payping/api";
import FinalStatus from "@/pages/DigitalAccount/Saman/Create/FinalStatus";
import AnchorSamanButtons from "@/pages/DigitalAccount/Saman/Create/Anchor/buttons";
import { setSamanUpdateRequestList } from "@payping/redux/actions";
import { closeId } from "@/assets/googleTrackIds";

export interface SamanCreateFormType {
  title: string;
  description?: string;
  transferMethod: TransferMethodEnum;
  transfer: SamanRowType[];
  iban: number;
  amount: number;
}

export interface SamanConfiguration {
  title: string;
  description?: string;
}

// eslint-disable-next-line react-refresh/only-export-components
export enum CreateSamanStepEnum {
  Configuration = 1,
  CreateTransfer = 2,
  Verification = 3,
  Final = 4,
}
export interface SamanOverviewType {
  totalAmount: string;
  totalWage: string;
  total: string;
}

const Create = () => {
  const { t } = useAppTranslation();
  const { step } = useParams<{ step: string }>() as { step: string };
  const stepNumber = Number(step) as CreateSamanStepEnum;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [formStepOne] = LegacyForm.useForm<SamanConfiguration>();
  const [formStepTwo] = LegacyForm.useForm<SamanRowType>();
  const [formStepThree] = LegacyForm.useForm<SamanCreateFormType>();
  const [formTransfer] = LegacyForm.useForm();
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [requestTitle, setRequestTitle] = useState<string>("");
  const [requestDescription, setRequestDescription] = useState<string>("");
  const [transferType, setTransferType] = useState<TransferMethodEnum>();
  const [openFinalDrawer, setOpenFinalDrawer] = useState(false);
  const [loadingStepTwo, setLoadingStepTwo] = useState(false);
  const [overviewData, setOverviewData] = useState<SamanOverviewType>({
    total: "",
    totalAmount: "",
    totalWage: "",
  });
  const { breakpoints } = usePaypingContext();
  const { lg } = breakpoints;

  useEffect(() => {
    return () => {
      formStepOne.resetFields();
      formStepTwo.resetFields();
      formStepThree.resetFields();
      dispatch(setSamanUpdateRequestList({}));
    };
  }, [dispatch, formStepOne, formStepThree, formStepTwo]);

  const nextStep = () => {
    navigate(generatePath(DashboardRoutes.digitalAccount.saman.create.url, { step: String(stepNumber + 1) }));
  };

  const prevStep = () => {
    navigate(generatePath(DashboardRoutes.digitalAccount.saman.create.url, { step: String(stepNumber - 1) }));
  };

  const steps = [
    {
      content: (
        <FirstStepSaman
          form={formStepOne}
          setRequestTitle={setRequestTitle}
          setRequestDescription={setRequestDescription}
        />
      ),
    },
    {
      content: (
        <SecondStepSaman
          formStepOne={formStepOne}
          formStepTwo={formStepTwo}
          setLoadingDelete={setLoadingDelete}
          setLoadingStepTwo={setLoadingStepTwo}
          next={nextStep}
          requestTitle={requestTitle}
        />
      ),
    },
    { content: <ThirdStepSaman form={formStepThree} setOverviewData={setOverviewData} formStepOne={formStepOne} /> },
  ];

  const getTitle = (title: CreateSamanStepEnum) => {
    switch (title) {
      case CreateSamanStepEnum.Configuration:
        return t("digitalAccount.samanCreate.configuration");
      case CreateSamanStepEnum.CreateTransfer:
        return t("digitalAccount.samanCreate.createTransfer");
      case CreateSamanStepEnum.Verification:
        return t("digitalAccount.samanCreate.verification");
      default:
        return undefined;
    }
  };

  const getContent = (content: CreateSamanStepEnum) => {
    switch (content) {
      case CreateSamanStepEnum.Configuration:
        return <div>{steps[0].content}</div>;
      case CreateSamanStepEnum.CreateTransfer:
        return <div>{steps[1].content}</div>;
      case CreateSamanStepEnum.Verification:
        return <div>{steps[2].content}</div>;
      default:
        return undefined;
    }
  };

  return (
    <>
      {stepNumber === CreateSamanStepEnum.Final ? (
        <FinalStatus openDrawer={openFinalDrawer} transferType={transferType} />
      ) : (
        <Drawer
          title={
            <Space>
              <Breadcrumb
                items={[{ title: t("digitalAccount.samanCreate.samanOperation") }, { title: getTitle(stepNumber) }]}
              />
              <Progress steps={3} percent={stepNumber * 30} format={(percent) => `${(percent || 30) / 30} از 3`} />
            </Space>
          }
          closeIcon={false}
          open
          placement={"bottom"}
          size={"100%"}
          extra={
            <Link to={DashboardRoutes.digitalAccount.yourAccount.url}>
              <Button type={"text"} icon={<Close width={14} height={14} />} data-google-track={closeId}></Button>
            </Link>
          }
          footer={
            !lg ? (
              <AnchorSamanButtons
                formStepOne={formStepOne}
                formStepTwo={formStepTwo}
                formStepThree={formStepThree}
                loadingDelete={loadingDelete}
                setOpenFinalDrawer={setOpenFinalDrawer}
                loadingStepTwo={loadingStepTwo}
                requestTitle={requestTitle}
                requestDescription={requestDescription}
                prev={prevStep}
                next={nextStep}
              />
            ) : null
          }
        >
          <Page>
            <SubLayout>
              <FormLayout
                firstChild={<div>{getContent(stepNumber)}</div>}
                secondChild={
                  <DataCardStepSaman
                    formStepOne={formStepOne}
                    formStepTwo={formStepTwo}
                    formStepThree={formStepThree}
                    loadingDelete={loadingDelete}
                    setOpenFinalDrawer={setOpenFinalDrawer}
                    loadingStepTwo={loadingStepTwo}
                    prev={prevStep}
                    next={nextStep}
                    overviewData={overviewData}
                    formTransfer={formTransfer}
                    requestTitle={requestTitle}
                    requestDesc={requestDescription}
                    transferType={transferType}
                    setTransferType={setTransferType}
                  />
                }
              />
            </SubLayout>
          </Page>
        </Drawer>
      )}
    </>
  );
};

export default Create;
